import { Immutable } from "immer";

import { templates } from "@reactivated";

import {
    CoreProductUuid,
    isoCoreProductUuid,
} from "@thelabnyc/tsischemas/esm/codecs";

import {
    IFormUUID,
    IReviewsProductTypeID,
    isoFormUUID,
    isoReviewsBrandID,
    isoReviewsProductTypeID,
} from "../../models/nominals";
import {
    ReviewsInfoMap,
    UUIDReviewsInfoMap,
    UUIDReviewsInfoObj,
} from "../../models/reviews";
import { type ISearchReviewArgs, ReviewSortOption } from "../../models/reviews";
import { check } from "../../models/utils";
import { type AppDispatch } from "../../store/reducers";
import { serializeFacetValues } from "../../utils/reviews";
import {
    parseSSRReviewResultsPage,
    parseSSRReviewsProducts,
    parseSSRSearchFacets,
    parseSSRWriteReviewTemplate,
    parseSSRWriteReviewTemplates,
} from "../../utils/ssr-data-parsers";
import { WriteReviewFormMode } from "./constants";
import {
    markSSRReviewsDataPrefilled,
    registerNewWriteReviewForm,
    reviewFormVariantChange,
    reviewSetCustomerInformationEmail,
    setFacetValues,
    setProductTypes,
    updateSorting,
} from "./reducers";
import { getDefaultFilterOptions } from "./selectors";
import { loadProducts } from "./thunks/loadProducts";
import { loadProductsByUUIDs } from "./thunks/loadProductsByUUIDs";
import { loadReviewTemplate } from "./thunks/loadReviewTemplate";
import { loadReviews } from "./thunks/loadReviews";

/**
 * Dispatch fulfilled thunk actions to pre-populate the Redux store with
 * SSR-fetched data for the ProductReviews (PDP) component.
 */
export const prefillSSRStoreData = (
    dispatch: AppDispatch,
    data: {
        products_data: string;
        reviews_data: string;
        facets_data: string;
    },
): void => {
    const products = parseSSRReviewsProducts(data.products_data);
    const reviewsPage = parseSSRReviewResultsPage(data.reviews_data);
    const facets = parseSSRSearchFacets(data.facets_data);

    if (products.length > 0) {
        dispatch(loadProductsByUUIDs.fulfilled(products, "", []));

        // Set filter defaults from products so the mount effect in
        // ProductReviews doesn't need to dispatch these (which would
        // trigger the reload effect and cause a redundant reviews fetch).
        const { productTypeIDs, productIDs, variantIDs } =
            getDefaultFilterOptions(products);
        dispatch(setProductTypes(productTypeIDs));
        dispatch(
            setFacetValues({
                product_id: serializeFacetValues(productIDs),
                product_variant_id: serializeFacetValues(variantIDs),
            }),
        );
        dispatch(markSSRReviewsDataPrefilled());
    }

    _prefillReviews(dispatch, reviewsPage, facets);
};

/**
 * Dispatch fulfilled thunk actions to pre-populate the Redux store with
 * SSR-fetched data for the standalone ReviewsPage component.
 *
 * Key differences from prefillSSRStoreData (PDP):
 * - Uses loadProducts.fulfilled (all brand products) instead of loadProductsByUUIDs
 * - Sets sort option and facet values from SSR context (which reflects URL params)
 * - Sets product types from the page's whitelist config
 */
export const prefillSSRReviewsPageData = (
    dispatch: AppDispatch,
    data: Immutable<
        Pick<
            templates.ReviewsPageTemplate,
            | "products_data"
            | "reviews_data"
            | "facets_data"
            | "brand_id"
            | "default_sort"
            | "product_type_whitelist_ids"
        >
    >,
): void => {
    const products = parseSSRReviewsProducts(data.products_data);
    const reviewsPage = parseSSRReviewResultsPage(data.reviews_data);
    const facets = parseSSRSearchFacets(data.facets_data);

    if (products.length > 0) {
        const brandID = isoReviewsBrandID.wrap(data.brand_id);
        dispatch(loadProducts.fulfilled(products, "", brandID));
    }

    // Set sort option from SSR context (already reflects URL ordering param
    // or page default_sort fallback).
    dispatch(updateSorting(data.default_sort as ReviewSortOption));

    // Set product type whitelist from SSR context
    let whitelistIDs: readonly IReviewsProductTypeID[] = [];
    try {
        whitelistIDs = (
            JSON.parse(data.product_type_whitelist_ids) as number[]
        ).map((id) => isoReviewsProductTypeID.wrap(id));
    } catch (e) {
        console.error("Failed to parse SSR product_type_whitelist_ids:", e);
    }
    if (whitelistIDs.length > 0) {
        dispatch(setProductTypes(whitelistIDs));
    }

    _prefillReviews(dispatch, reviewsPage, facets);
};

/**
 * Shared helper: dispatch loadReviews.fulfilled to pre-populate reviews + facets.
 */
const _prefillReviews = (
    dispatch: AppDispatch,
    reviewsPage: ReturnType<typeof parseSSRReviewResultsPage>,
    facets: ReturnType<typeof parseSSRSearchFacets>,
): void => {
    if (reviewsPage) {
        dispatch(
            loadReviews.fulfilled(
                {
                    reviews: reviewsPage.results,
                    facets: facets,
                    hasMore: reviewsPage.next !== null,
                    page: 1,
                    loadedFacetValues: {} as ISearchReviewArgs,
                },
                "",
                {
                    brandID: isoReviewsBrandID.wrap(0),
                    query: {} as ISearchReviewArgs,
                },
            ),
        );
    }
};

/**
 * Dispatch fulfilled thunk actions to pre-populate the Redux store with
 * SSR-fetched data for the WriteReview form.
 *
 * Pre-fills products and the review template for the first product so the
 * form renders immediately without client-side API calls.
 */
export const prefillSSRWriteReviewData = (
    dispatch: AppDispatch,
    data: {
        products_data: string;
        review_template_data: string;
        brand_id: number;
        formUUID: IFormUUID;
    },
): void => {
    const products = parseSSRReviewsProducts(data.products_data);
    const template = parseSSRWriteReviewTemplate(data.review_template_data);

    if (products.length > 0) {
        const brandID = isoReviewsBrandID.wrap(data.brand_id);
        dispatch(loadProducts.fulfilled(products, "", brandID));

        // Register the form in Redux state so SingleProductWriteReviewForm
        // doesn't need to dispatch this in its mount effect.
        dispatch(
            registerNewWriteReviewForm({
                formUUID: data.formUUID,
                mode: WriteReviewFormMode.FORM_OPEN,
            }),
        );
    }

    // Pre-fill the review template for the first product.
    if (template && products.length > 0) {
        const firstProduct = products[0];
        const productID = firstProduct.id;
        dispatch(
            loadReviewTemplate.fulfilled(
                { formUUID: data.formUUID, productID, template },
                "",
                { formUUID: data.formUUID, productID },
            ),
        );
    }
};

/**
 * Dispatch fulfilled thunk actions to pre-populate the Redux store with
 * SSR-fetched data for the MultiProductWriteReviewForm.
 *
 * Pre-fills products, review templates for each product, registers form
 * state for each product/variant combo, and sets customer email.
 */
export const prefillSSRMultiProductWriteReviewData = (
    dispatch: AppDispatch,
    data: {
        products_data: string;
        review_templates_data: string;
        reviews_info_map: string;
        email: string;
    },
): void => {
    const products = parseSSRReviewsProducts(data.products_data);
    const templatePairs = parseSSRWriteReviewTemplates(
        data.review_templates_data,
    );

    if (products.length === 0) {
        return;
    }

    // Pre-fill products via loadProductsByUUIDs (not loadProducts — this
    // component loads by UUID, not by brand).
    dispatch(loadProductsByUUIDs.fulfilled(products, "", []));

    // Build a map of productID → template for quick lookup.
    const templatesByProductID = new Map(
        templatePairs.map((pair) => [pair.productID, pair.template]),
    );

    // Parse the reviews info map to determine form UUIDs and variants.
    let infoMap: UUIDReviewsInfoMap;
    try {
        const rawMap = check(
            UUIDReviewsInfoObj.decode(JSON.parse(data.reviews_info_map)),
        );
        infoMap = Object.entries(rawMap)
            .map(([k, v]): [CoreProductUuid, ReviewsInfoMap] => {
                return [isoCoreProductUuid.wrap(k), v];
            })
            .reduce(
                (m, [k, v]) => m.set(k, v),
                new Map<CoreProductUuid, ReviewsInfoMap>(),
            );
    } catch (e) {
        console.error("Failed to parse SSR reviews_info_map:", e);
        return;
    }

    // Register forms and pre-fill templates for each product/variant combo,
    // mirroring the mount effect in MultiProductWriteReviewForm.
    for (const product of products) {
        if (!product.uuid) {
            continue;
        }
        const reviewsMap = infoMap.get(product.uuid);
        const template = templatesByProductID.get(product.id);

        if (reviewsMap && reviewsMap.reviewsProductVariantIDs.length > 0) {
            // Product has variants — register a form per variant.
            for (const variantID of reviewsMap.reviewsProductVariantIDs) {
                const formUUID = isoFormUUID.wrap(
                    `${product.uuid}__${variantID}`,
                );
                dispatch(
                    registerNewWriteReviewForm({
                        formUUID,
                        mode: WriteReviewFormMode.FORM_OPEN,
                    }),
                );
                if (template) {
                    dispatch(
                        loadReviewTemplate.fulfilled(
                            { formUUID, productID: product.id, template },
                            "",
                            { formUUID, productID: product.id },
                        ),
                    );
                }
                // Set variant selection on the form.
                const variantInfo = product.variants.find(
                    (v) => v.id === variantID,
                );
                if (variantInfo) {
                    dispatch(
                        reviewFormVariantChange({
                            formUUID,
                            selectedVariant: variantInfo.slug,
                        }),
                    );
                }
            }
        } else {
            // No variants — single form keyed by UUID.
            const formUUID = isoFormUUID.wrap(String(product.uuid));
            dispatch(
                registerNewWriteReviewForm({
                    formUUID,
                    mode: WriteReviewFormMode.FORM_OPEN,
                }),
            );
            if (template) {
                dispatch(
                    loadReviewTemplate.fulfilled(
                        { formUUID, productID: product.id, template },
                        "",
                        { formUUID, productID: product.id },
                    ),
                );
            }
        }
    }

    // Set customer email.
    if (data.email) {
        dispatch(reviewSetCustomerInformationEmail(data.email));
    }
};
